import { createSlice, SerializedError } from "@reduxjs/toolkit";

import {
  NormalizedCarbonEmissionsData,
  normalizeCarbonEmissionsData
} from "./normalizeInput";
import {
  fetchOrganizationCarbonEmissionsThunk,
  fetchPreviousYearCarbonEmissionsThunk,
  fetchCurrentYearCarbonEmissionsThunk,
  fetchLastSixMonthsCarbonEmissionsThunk
} from "./thunks/fetchOrganizationCarbonEmissions.thunk";

export type OrganizationCarbonEmissionsState = {
  data: NormalizedCarbonEmissionsData | null;
  isLoading: boolean;
  error?: SerializedError;
};

export type CarbonEmissionsSliceState = {
  byOrganizationId: Record<
    string,
    OrganizationCarbonEmissionsState | undefined
  >;
};

export const initialState: CarbonEmissionsSliceState = {
  byOrganizationId: {}
};

const carbonEmissionsSlice = createSlice({
  name: "carbonEmissions",
  initialState,
  reducers: {
    clearCarbonEmissionsData(state, action) {
      const { organizationId } = action.payload;
      if (organizationId) {
        state.byOrganizationId[organizationId] = undefined;
      } else {
        state.byOrganizationId = {};
      }
    },
    setOrganizationLoading(state, action) {
      const { organizationId, isLoading } = action.payload;
      if (organizationId) {
        // Create or update the organization entry
        if (!state.byOrganizationId[organizationId]) {
          state.byOrganizationId[organizationId] = {
            data: null,
            isLoading: isLoading,
            error: undefined
          };
        } else {
          state.byOrganizationId[organizationId].isLoading = isLoading;
        }
      }
    }
  },
  extraReducers: builder => {
    // Helper function to initialize or get organization state
    const getOrCreateOrgState = (
      state: CarbonEmissionsSliceState,
      organizationId: string
    ) => {
      // Create the organization entry if it doesn't exist
      if (!state.byOrganizationId[organizationId]) {
        state.byOrganizationId[organizationId] = {
          data: null,
          isLoading: false,
          error: undefined
        };
      }
      return state.byOrganizationId[organizationId];
    };

    // Basic carbon emissions fetch
    builder.addCase(
      fetchOrganizationCarbonEmissionsThunk.fulfilled,
      (state, action) => {
        const { organizationId, data } = action.payload;
        const orgState = getOrCreateOrgState(state, organizationId);
        orgState.isLoading = false;

        const existingData = orgState.data;
        orgState.data = normalizeCarbonEmissionsData(
          data,
          organizationId,
          undefined,
          existingData
        );
      }
    );

    builder.addCase(
      fetchOrganizationCarbonEmissionsThunk.rejected,
      (state, action) => {
        // If the request has an organizationId in the payload, use that
        const payload = action.payload as
          | { organizationId?: string }
          | undefined;
        const organizationId = payload?.organizationId || "";

        if (organizationId) {
          // Get or create the organization state
          const orgState = getOrCreateOrgState(state, organizationId);

          // Update loading and error state
          orgState.isLoading = false;
          orgState.error = action.error;
        }
      }
    );

    // Previous year data
    builder.addCase(
      fetchPreviousYearCarbonEmissionsThunk.fulfilled,
      (state, action) => {
        const { organizationId, data, timeframe } = action.payload;
        const orgState = getOrCreateOrgState(state, organizationId);
        orgState.isLoading = false;

        const existingData = orgState.data;
        orgState.data = normalizeCarbonEmissionsData(
          data,
          organizationId,
          timeframe,
          existingData
        );
      }
    );

    builder.addCase(
      fetchPreviousYearCarbonEmissionsThunk.rejected,
      (state, action) => {
        // If the request has an organizationId in the payload, use that
        const payload = action.payload as
          | { organizationId?: string }
          | undefined;
        const organizationId = payload?.organizationId || "";

        if (organizationId) {
          // Get or create the organization state
          const orgState = getOrCreateOrgState(state, organizationId);

          orgState.isLoading = false;
          orgState.error = action.error;
        }
      }
    );

    // Current year data
    builder.addCase(
      fetchCurrentYearCarbonEmissionsThunk.fulfilled,
      (state, action) => {
        const { organizationId, data, timeframe } = action.payload;
        const orgState = getOrCreateOrgState(state, organizationId);

        orgState.isLoading = false;

        const existingData = orgState.data;
        orgState.data = normalizeCarbonEmissionsData(
          data,
          organizationId,
          timeframe,
          existingData
        );
      }
    );

    builder.addCase(
      fetchCurrentYearCarbonEmissionsThunk.rejected,
      (state, action) => {
        // If the request has an organizationId in the payload, use that
        const payload = action.payload as
          | { organizationId?: string }
          | undefined;
        const organizationId = payload?.organizationId || "";

        if (organizationId) {
          const orgState = getOrCreateOrgState(state, organizationId);

          orgState.isLoading = false;
          orgState.error = action.error;
        }
      }
    );

    // Last six months data
    builder.addCase(
      fetchLastSixMonthsCarbonEmissionsThunk.fulfilled,
      (state, action) => {
        if (!action.payload) return;
        const { organizationId, data, timeframe } = action.payload;
        const orgState = getOrCreateOrgState(state, organizationId);

        orgState.isLoading = false;

        const existingData = orgState.data;
        orgState.data = normalizeCarbonEmissionsData(
          data,
          organizationId,
          timeframe,
          existingData
        );
      }
    );

    builder.addCase(
      fetchLastSixMonthsCarbonEmissionsThunk.rejected,
      (state, action) => {
        const payload = action.payload as
          | { organizationId?: string }
          | undefined;
        const organizationId = payload?.organizationId || "";

        if (organizationId) {
          const orgState = getOrCreateOrgState(state, organizationId);

          orgState.isLoading = false;
          orgState.error = action.error;
        }
      }
    );
  }
});

export const { clearCarbonEmissionsData, setOrganizationLoading } =
  carbonEmissionsSlice.actions;
export const carbonEmissionsReducer = carbonEmissionsSlice.reducer;
